import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import * as BlinkCardSDK from '@microblink/blinkcard-in-browser-sdk';
import { DetectionStatus } from '@microblink/blinkcard-in-browser-sdk';
import { SDKError } from '@microblink/blinkcard-in-browser-sdk/src/MicroblinkSDK/SDKError';
import { BlinkCardRecognizerResult } from '@microblink/blinkcard-in-browser-sdk/src/Recognizers/BlinkCard/BlinkCardRecognizer';
import { DialogOverlay } from '@reach/dialog';
import { useMachine } from '@xstate/react';
import { cardCaptureMachine } from 'machines/cardCaptureMachine';
import styled from 'styled-components';

import { AltButton, IconButton } from 'chime-ui/components/Button';
import {
  DebitCardNumber,
  MicroBlinkIcon,
  WhiteCloseIcon,
} from 'chime-ui/components/Icons';
import Spinner from 'chime-ui/components/Spinner';
import { Text } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';

import { startDebitCardScan } from 'components/SubPages/ClaimInfo/features/startDebitCardScan';

const assetBaseUrl = process.env.NODE_ENV === 'development' ? '' : '/pay';

const cameraFeedbackText = {
  cardNumber: 'Scan the card number',
  alignCameraToFrame: 'Align the card into frame',
};

type BlinkCardProps = {
  onScanSuccess: (recognizer: BlinkCardRecognizerResult) => void;
  onDismiss: () => void;
  onError: (errorMessage: string, errorCode?: string) => void;
};

function BlinkCard({
  onScanSuccess,
  onDismiss,
  onError,
}: BlinkCardProps): ReactElement {
  const mountedRef = useRef(false);
  // BlinkCard UI Element
  const cameraFeedRef = useRef<HTMLVideoElement>(null);

  const [blinkCardWasmSdk, setBlinkCardWasmSdk] = useState(null);
  const isLoadingSdk = !blinkCardWasmSdk;
  const [cardCaptureState, cardCaptureSend] = useMachine(cardCaptureMachine);

  // Create a cancel object that we can send to startDebitCardScan
  // so we have access to videoRecognizer
  const [cancelScan, setCancelScan] = useState(null);

  const handleScanSuccess = useCallback(
    result => {
      onScanSuccess(result);
    },
    [onScanSuccess]
  );

  const handleDismiss = () => {
    cancelScan?.run();
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleError = useCallback(
    (errorMessage: string, errorCode?: string) => {
      if (onDismiss) {
        onDismiss();
      }

      if (onError) {
        onError(errorMessage, errorCode);
      }
    },
    [onError, onDismiss]
  );

  // Track if the component is mounted so that we can stop state updates
  // if the user closes the modal and the SDK loads async.
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!process.env.BLINK_CARD_LICENSE) {
      return;
    }

    // This initializes blinkCard wasm SDk - it runs as a webworker
    // and it only needs to be initialized once.
    if (!blinkCardWasmSdk) {
      const loadSettings = new BlinkCardSDK.WasmSDKLoadSettings(
        process.env.BLINK_CARD_LICENSE
      );
      loadSettings.engineLocation = `${window.location.origin}${assetBaseUrl}/blinkCard/resources`;
      loadSettings.workerLocation =
        loadSettings.engineLocation + '/BlinkCardWasmSDK.worker.min.js';

      BlinkCardSDK.loadWasmModule(loadSettings).then(
        (wasmSDK: BlinkCardSDK.WasmSDK) => {
          if (mountedRef.current) {
            setBlinkCardWasmSdk(wasmSDK);
          }
        },
        (error: SDKError) => {
          // Error happened during the initialization of the SDK
          handleError(error.message, error.code);
        }
      );
    } else if (cardCaptureState.matches('idle')) {
      // Metadata callbacks from microblink SDk
      // @link: https://github.com/blinkcard/blinkcard-in-browser/blob/master/src/MicroblinkSDK/MetadataCallbacks.ts
      const recognizerRunnerCallbacks = {
        onQuadDetection: (quad: BlinkCardSDK.DisplayableQuad) => {
          if (!cameraFeedRef.current) {
            return;
          }

          const detectionStatus = quad.detectionStatus;
          switch (detectionStatus) {
            case DetectionStatus.CameraTooHigh:
            case DetectionStatus.CameraTooNear:
            case DetectionStatus.DocumentTooCloseToEdge:
            case DetectionStatus.Partial:
            case DetectionStatus.CameraAtAngle:
              cardCaptureSend('ALIGN_CAMERA');
              break;
            default:
              cardCaptureSend('RESUME');
              break;
          }
        },
      };

      cardCaptureSend('START');

      startDebitCardScan(
        blinkCardWasmSdk,
        cameraFeedRef.current,
        handleScanSuccess,
        setCancelScan,
        recognizerRunnerCallbacks,
        handleError
      );
    }
  }, [
    blinkCardWasmSdk,
    handleScanSuccess,
    handleError,
    cardCaptureSend,
    cancelScan,
    cardCaptureState,
  ]);

  if (!process.env.BLINK_CARD_LICENSE) {
    handleError('Debit card capture is unavailable');
    return null;
  }

  return (
    <BlinkCardLayout>
      <CloseButtonLayout>
        <IconButton icon={<WhiteCloseIcon />} onClick={handleDismiss} />
      </CloseButtonLayout>
      <CameraFeed
        aria-label={'camera video feed'}
        ref={cameraFeedRef}
        playsInline={true}
      />
      <BlinkCardContentLayout>
        <CameraFeedbackTextLayout>
          <Text size={'standard'} color={colors.white}>
            {cameraFeedbackText[cardCaptureState.value as string]}
          </Text>
        </CameraFeedbackTextLayout>
        <CardOverlayLayout>
          {isLoadingSdk ? (
            <Spinner />
          ) : (
            <>
              <CardOverlay />
              <DebitCardNumberLayout>
                <DebitCardNumber />
              </DebitCardNumberLayout>
            </>
          )}
        </CardOverlayLayout>
        <EnterManuallyButtonLayout>
          <AltButton onClick={handleDismiss}>Enter manually instead</AltButton>
        </EnterManuallyButtonLayout>
        <MicroBlinkIcon />
      </BlinkCardContentLayout>
    </BlinkCardLayout>
  );
}

const CameraFeedbackTextLayout = styled.div`
  z-index: 1;
`;

const EnterManuallyButtonLayout = styled.div`
  width: 100%;
  max-width: 375px;
  padding: 0 1.5rem;
  z-index: 1;
`;

const CloseButtonLayout = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
  z-index: 1;
`;

const CardOverlay = styled.div`
  border: ${colors.white} 2px solid;
  border-radius: 0.5rem;
  box-shadow: 0 0 0 50rem rgba(1, 1, 1, 0.3);
  height: 100%;
  max-height: 205px;
  transition: all 0.3s ease-in 0s;
  width: 327px;
`;

const DebitCardNumberLayout = styled.div`
  position: absolute;
  bottom: 3rem;
`;

const BlinkCardLayout = styled(DialogOverlay)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: black;
  height: 100%;
  width: 100%;
`;

const BlinkCardContentLayout = styled.div`
  width: 100%;
  max-width: 900px;
  position: absolute;
  inset: 0;
  align-content: center;
  display: grid;
  grid-gap: 2rem;
  grid-template-rows: repeat(3, min-content);
  justify-items: center;

  @media (min-height: 290px) and (max-height: 499px) and (orientation: landscape) {
    padding-top: 8rem;
    grid-gap: 0.5rem;
  }
`;

const CardOverlayLayout = styled.div`
  display: flex;
  height: 230px;
  position: relative;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const CameraFeed = styled.video`
  width: 100%;
`;

export default BlinkCard;
